import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ComponentErrorBoundary } from '@/components/shared/ComponentErrorBoundary';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { DataStatePanel } from '@/components/shared/DataStatePanel';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { TemplateBuilderForm } from '@/components/templates/TemplateBuilderForm';
import { templateRoutes } from '@/data/routes';
import {
  cloneTemplateBuilderDraft,
  createDefaultTemplateDraft,
  createTemplateDraftFromMassDraft,
  createTemplateDraftFromState,
  createTemplateDraftSignature,
  hasMeaningfulTemplateDraftContent,
  normalizeTemplateBuilderDraft,
} from '@/features/templates/builder';
import { clearTemplateDraftCache, readTemplateDraftCache, writeTemplateDraftCache } from '@/features/templates/draftStorage';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import {
  deleteTemplateRecord,
  deleteTemplateWorkingDraft,
  loadTemplateWorkingDraft,
  markTemplateWorkingDraftRestored,
  saveTemplateBuilderDraft,
  saveTemplateWorkingDraft,
  setTemplateActiveState,
  type TemplateWorkingDraftPayload,
} from '@/lib/repositories/templateRepository';
import type { EventBuilderDraft, TemplateBuilderDraft, TemplateDraftRecord } from '@/types/domain';

type AutosaveState = 'idle' | 'saving' | 'saved' | 'restored' | 'error';
type DraftLoadStatus = 'loading' | 'ready' | 'error';
type DraftRestoreSource = 'cloud' | 'local' | null;

interface TemplateBuilderLocationState {
  fromMassDraft?: EventBuilderDraft;
  initialTemplateDraft?: TemplateBuilderDraft;
  skipStoredCreateDraft?: boolean;
  sourceEventId?: string;
  sourceTemplateId?: string;
  templateSavedAt?: string;
}

interface SavedTemplateDraftResult {
  draft: TemplateBuilderDraft | null;
  error: string | null;
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId: number | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error(message));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  }
}

function validateTemplateDraft(draft: TemplateBuilderDraft): string | null {
  const allSlots = draft.parties.flatMap((party) => party.slots);

  if (!draft.name.trim()) {
    return 'Template name is required.';
  }

  if (allSlots.some((slot) => !slot.weaponId || !slot.weaponName || !slot.imagePath)) {
    return 'Every configured template slot needs a weapon selected from the catalog.';
  }

  return null;
}

function getLatestDraftSource(
  cloudDraft: TemplateDraftRecord | null,
  localDraft: ReturnType<typeof readTemplateDraftCache>,
): DraftRestoreSource {
  if (!cloudDraft && !localDraft) {
    return null;
  }

  if (cloudDraft && !localDraft) {
    return 'cloud';
  }

  if (!cloudDraft && localDraft) {
    return 'local';
  }

  return new Date(cloudDraft!.updatedAt).getTime() >= new Date(localDraft!.savedAt).getTime() ? 'cloud' : 'local';
}

function TemplateBuilderWorkspace(): JSX.Element {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { state, isLoading, error, refreshData } = useGuildData();
  const locationState = (location.state as TemplateBuilderLocationState | null) ?? null;
  const [draft, setDraft] = useState<TemplateBuilderDraft>(createDefaultTemplateDraft());
  const [draftRecord, setDraftRecord] = useState<TemplateDraftRecord | null>(null);
  const [draftLoadStatus, setDraftLoadStatus] = useState<DraftLoadStatus>('loading');
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveState>('idle');
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [lastAutosavedAt, setLastAutosavedAt] = useState<string | undefined>();
  const [lastTemplateSavedAt, setLastTemplateSavedAt] = useState<string | undefined>();
  const [draftRestoredFrom, setDraftRestoredFrom] = useState<DraftRestoreSource>(null);
  const [baselineSignature, setBaselineSignature] = useState('');
  const [lastAutosavedSignature, setLastAutosavedSignature] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [guardSuppressed, setGuardSuppressed] = useState(false);
  const completedSessionKeyRef = useRef<string | null>(null);
  const initializationRequestIdRef = useRef(0);

  const currentTemplate = useMemo(
    () => state.templates.find((template) => template.id === templateId) ?? null,
    [state.templates, templateId],
  );

  const savedTemplateDraftResult = useMemo<SavedTemplateDraftResult>(() => {
    if (!templateId) {
      return { draft: null, error: null };
    }

    try {
      return {
        draft: createTemplateDraftFromState(state, templateId),
        error: null,
      };
    } catch (nextError) {
      return {
        draft: null,
        error: nextError instanceof Error ? nextError.message : 'Failed to prepare the saved template composition.',
      };
    }
  }, [state, templateId]);

  const savedTemplateDraft = savedTemplateDraftResult.draft;
  const effectiveTemplateId = templateId ?? draft.id;
  const workingSignature = useMemo(() => createTemplateDraftSignature(draft), [draft]);
  const hasMeaningfulWorkingDraft = hasMeaningfulTemplateDraftContent(draft);
  const hasSavedTemplate = Boolean(effectiveTemplateId);
  const isDraftReady = draftLoadStatus === 'ready';
  const hasUnsavedChanges = isDraftReady
    ? hasSavedTemplate
      ? workingSignature !== baselineSignature
      : hasMeaningfulWorkingDraft
    : false;
  const sessionKey = templateId ? `template:${templateId}:${location.key}` : `create:${location.key}`;

  useUnsavedChangesGuard({
    when: !guardSuppressed && (hasUnsavedChanges || autosaveStatus === 'saving'),
    message: 'You have unsaved template changes. Leaving this page now can discard the current working draft from this view.',
  });

  useEffect(() => {
    setGuardSuppressed(false);
  }, [location.key, templateId]);

  useEffect(() => {
    if (!profile || isLoading) {
      return;
    }

    if (completedSessionKeyRef.current === sessionKey && draftLoadStatus === 'ready') {
      return;
    }

    const requestId = initializationRequestIdRef.current + 1;
    let isActive = true;
    initializationRequestIdRef.current = requestId;
    setDraftLoadStatus('loading');
    setDraftLoadError(null);
    setFormError(null);

    const initializeSession = async () => {
      try {
        const inlineTemplateDraft =
          locationState?.initialTemplateDraft ??
          (locationState?.fromMassDraft ? createTemplateDraftFromMassDraft(locationState.fromMassDraft) : null);
        const shouldSkipStoredCreateDraft = Boolean(locationState?.skipStoredCreateDraft && !templateId);
        const savedDraft = templateId ? savedTemplateDraft : null;
        const cloudDraft = shouldSkipStoredCreateDraft
          ? null
          : await withTimeout(
              loadTemplateWorkingDraft(profile.id, templateId),
              10000,
              'Loading the cloud template draft took too long. Please retry the template editor.',
            );
        const localDraft = shouldSkipStoredCreateDraft ? null : readTemplateDraftCache(templateId);
        const latestDraftSource = getLatestDraftSource(cloudDraft, localDraft);

        let nextDraft = createDefaultTemplateDraft();
        let nextDraftRecord: TemplateDraftRecord | null = null;
        let nextRestoredFrom: DraftRestoreSource = null;
        let nextAutosaveStatus: AutosaveState = 'idle';
        let nextLastAutosavedAt: string | undefined;
        let nextLastAutosavedSignature = '';
        const nextLastTemplateSavedAt = locationState?.templateSavedAt ?? currentTemplate?.updatedAt;

        if (inlineTemplateDraft && (!templateId || inlineTemplateDraft.id === templateId)) {
          nextDraft = normalizeTemplateBuilderDraft(inlineTemplateDraft);
          nextAutosaveStatus = hasMeaningfulTemplateDraftContent(nextDraft) ? 'idle' : 'saved';
          nextLastAutosavedSignature = shouldSkipStoredCreateDraft ? '' : createTemplateDraftSignature(nextDraft);
        } else if (latestDraftSource === 'cloud' && cloudDraft) {
          nextDraft = normalizeTemplateBuilderDraft({
            id: cloudDraft.templateId ?? templateId,
            name: cloudDraft.name,
            description: cloudDraft.description,
            isActive: cloudDraft.isActive,
            parties: cloudDraft.parties,
          });
          nextDraftRecord = cloudDraft;
          nextRestoredFrom = 'cloud';
          nextAutosaveStatus = 'restored';
          nextLastAutosavedAt = cloudDraft.lastAutosavedAt;
          nextLastAutosavedSignature = createTemplateDraftSignature(nextDraft);
          void markTemplateWorkingDraftRestored(cloudDraft.id).catch(() => {
            // Best-effort only. The editor should keep loading even if this update fails.
          });
        } else if (latestDraftSource === 'local' && localDraft) {
          nextDraft = normalizeTemplateBuilderDraft(localDraft.draft);
          nextRestoredFrom = 'local';
          nextAutosaveStatus = 'restored';
          nextLastAutosavedAt = localDraft.savedAt;
        } else if (savedDraft) {
          nextDraft = normalizeTemplateBuilderDraft(savedDraft);
          nextAutosaveStatus = 'saved';
          nextLastAutosavedSignature = createTemplateDraftSignature(nextDraft);
        }

        if (!isActive || initializationRequestIdRef.current !== requestId) {
          return;
        }

        setDraft(nextDraft);
        setDraftRecord(nextDraftRecord);
        setDraftRestoredFrom(nextRestoredFrom);
        setAutosaveStatus(nextAutosaveStatus);
        setAutosaveError(null);
        setLastAutosavedAt(nextLastAutosavedAt);
        setLastTemplateSavedAt(nextLastTemplateSavedAt);
        setLastAutosavedSignature(nextLastAutosavedSignature);
        setBaselineSignature(savedDraft ? createTemplateDraftSignature(savedDraft) : '');
        completedSessionKeyRef.current = sessionKey;
        setDraftLoadStatus('ready');
      } catch (nextError) {
        if (!isActive || initializationRequestIdRef.current !== requestId) {
          return;
        }

        setDraftLoadStatus('error');
        setDraftLoadError(nextError instanceof Error ? nextError.message : 'Failed to load the template editor session.');
      }
    };

    void initializeSession();

    return () => {
      isActive = false;
    };
  }, [draftLoadStatus, isLoading, locationState, profile, savedTemplateDraft, sessionKey, templateId]);

  useEffect(() => {
    if (draftLoadStatus !== 'ready') {
      return;
    }

    writeTemplateDraftCache(templateId, draft);
  }, [draft, draftLoadStatus, templateId]);

  useEffect(() => {
    if (!profile || draftLoadStatus !== 'ready') {
      return;
    }

    if (!effectiveTemplateId && !hasMeaningfulWorkingDraft) {
      return;
    }

    if (workingSignature === lastAutosavedSignature) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const payload: TemplateWorkingDraftPayload = {
        draftId: draftRecord?.id,
        templateId: effectiveTemplateId,
        sourceTemplateId: locationState?.sourceTemplateId ?? (templateId ? templateId : undefined),
        sourceEventId: locationState?.sourceEventId,
        templateUpdatedAtSnapshot: currentTemplate?.updatedAt,
        draft,
      };

      setAutosaveStatus('saving');
      setAutosaveError(null);

      void saveTemplateWorkingDraft(profile.id, payload)
        .then((savedDraftRecord) => {
          setDraftRecord(savedDraftRecord);
          setLastAutosavedAt(savedDraftRecord.lastAutosavedAt);
          setLastAutosavedSignature(workingSignature);
          setAutosaveStatus('saved');
          setDraftRestoredFrom(null);
        })
        .catch((nextError) => {
          setAutosaveStatus('error');
          setAutosaveError(nextError instanceof Error ? nextError.message : 'Failed to autosave the template draft.');
        });
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    currentTemplate?.updatedAt,
    draft,
    draftLoadStatus,
    draftRecord?.id,
    effectiveTemplateId,
    hasMeaningfulWorkingDraft,
    lastAutosavedSignature,
    locationState?.sourceEventId,
    locationState?.sourceTemplateId,
    profile,
    templateId,
    workingSignature,
  ]);

  if (isLoading && templateId && !currentTemplate) {
    return (
      <AppShell>
        <DataStatePanel title="Loading template workspace" description="Fetching the saved composition and any cloud draft recovery state." />
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <DataStatePanel title="Unable to load template tools" description={error} actionLabel="Retry" onAction={() => void refreshData()} />
      </AppShell>
    );
  }

  if (savedTemplateDraftResult.error) {
    return (
      <AppShell>
        <DataStatePanel
          title="Unable to prepare this saved template"
          description={savedTemplateDraftResult.error}
          actionLabel="Retry"
          onAction={() => {
            completedSessionKeyRef.current = null;
            void refreshData();
          }}
        />
      </AppShell>
    );
  }

  if (templateId && !currentTemplate && !savedTemplateDraft && draftLoadStatus !== 'loading') {
    return (
      <AppShell>
        <EmptyState title="Template not found" description="This saved composition no longer exists or is unavailable to your role." />
      </AppShell>
    );
  }

  if (draftLoadStatus === 'loading') {
    return (
      <AppShell>
        <DataStatePanel
          title="Preparing your template session"
          description="Restoring the latest working draft and keeping the saved template separate from live edits."
        />
      </AppShell>
    );
  }

  if (draftLoadStatus === 'error') {
    return (
      <AppShell>
        <DataStatePanel
          title="Unable to restore the template session"
          description={draftLoadError ?? 'An unexpected error occurred while opening the template workspace.'}
          actionLabel="Retry"
          onAction={() => {
            completedSessionKeyRef.current = null;
            void refreshData();
          }}
        />
      </AppShell>
    );
  }

  const handleSave = async (createNew = false) => {
    if (!profile) {
      return;
    }

    setFormError(null);
    const validationError = validateTemplateDraft(draft);

    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!createNew && templateId && currentTemplate?.updatedAt && draftRecord?.templateUpdatedAtSnapshot) {
      const serverSavedAt = new Date(currentTemplate.updatedAt).getTime();
      const draftSnapshotSavedAt = new Date(draftRecord.templateUpdatedAtSnapshot).getTime();

      if (serverSavedAt > draftSnapshotSavedAt) {
        setFormError(
          'This template was updated after your current draft session started. Refresh the editor or use Save As New to avoid overwriting a newer saved version.',
        );
        return;
      }
    }

    setIsSavingTemplate(true);

    try {
      const savedTemplate = await saveTemplateBuilderDraft(
        createNew ? cloneTemplateBuilderDraft(draft, { asNew: true, nextName: draft.name }) : draft,
        profile.id,
        { createNew },
      );
      const nextTemplateId = savedTemplate.templateId;
      const savedAt = savedTemplate.savedAt;
      const nextDraft = savedTemplate.draft;

      const nextDraftRecord = await saveTemplateWorkingDraft(profile.id, {
        draftId: draftRecord?.id,
        templateId: nextTemplateId,
        sourceTemplateId: locationState?.sourceTemplateId ?? templateId,
        sourceEventId: locationState?.sourceEventId,
        templateUpdatedAtSnapshot: savedAt,
        draft: nextDraft,
      });

      if (!templateId || createNew) {
        clearTemplateDraftCache(templateId);
        clearTemplateDraftCache(undefined);
        writeTemplateDraftCache(nextTemplateId, nextDraft);
        setGuardSuppressed(true);
        navigate(templateRoutes.edit(nextTemplateId), {
          replace: true,
          state: {
            initialTemplateDraft: nextDraft,
            templateSavedAt: savedAt,
            sourceTemplateId: locationState?.sourceTemplateId ?? templateId,
            sourceEventId: locationState?.sourceEventId,
          } satisfies TemplateBuilderLocationState,
        });
      } else {
        writeTemplateDraftCache(templateId, nextDraft);
        setDraft(nextDraft);
      }

      setDraftRecord(nextDraftRecord);
      setBaselineSignature(createTemplateDraftSignature(nextDraft));
      setLastTemplateSavedAt(savedAt);
      setLastAutosavedAt(nextDraftRecord.lastAutosavedAt);
      setLastAutosavedSignature(createTemplateDraftSignature(nextDraft));
      setAutosaveStatus('saved');
      setAutosaveError(null);
      setDraftRestoredFrom(null);
      void refreshData();
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleStartNew = async () => {
    const proceed =
      !hasUnsavedChanges ||
      window.confirm('Start a brand new template? Your current working draft will stay saved in the cloud, but this page will open a fresh template builder.');

    if (!proceed) {
      return;
    }

    if (profile) {
      await deleteTemplateWorkingDraft(profile.id, undefined).catch(() => {
        // Best-effort cleanup only.
      });
    }

    clearTemplateDraftCache(undefined);
    setGuardSuppressed(true);
    navigate(templateRoutes.create, {
      state: {
        initialTemplateDraft: createDefaultTemplateDraft(),
        skipStoredCreateDraft: true,
      } satisfies TemplateBuilderLocationState,
    });
  };

  const handleDuplicate = () => {
    const proceed =
      !hasUnsavedChanges ||
      window.confirm('Open this composition as a new template draft? Your current working draft stays saved, and the duplicate opens as a separate template session.');

    if (!proceed) {
      return;
    }

    const duplicatedDraft = cloneTemplateBuilderDraft(draft, {
      asNew: true,
      nextName: draft.name.trim() ? `${draft.name.trim()} Copy` : '',
    });

    setGuardSuppressed(true);
    navigate(templateRoutes.create, {
      state: {
        initialTemplateDraft: duplicatedDraft,
        skipStoredCreateDraft: true,
        sourceTemplateId: effectiveTemplateId,
      } satisfies TemplateBuilderLocationState,
    });
  };

  const handleToggleArchive = async () => {
    if (!profile || !effectiveTemplateId) {
      return;
    }

    setIsTogglingArchive(true);

    try {
      await setTemplateActiveState(effectiveTemplateId, profile.id, !draft.isActive);
      const nextDraft = { ...draft, isActive: !draft.isActive };
      setDraft(nextDraft);
      setLastTemplateSavedAt(new Date().toISOString());
      setBaselineSignature(createTemplateDraftSignature(nextDraft));
      void refreshData();
    } finally {
      setIsTogglingArchive(false);
    }
  };

  const handleDelete = async () => {
    if (!effectiveTemplateId) {
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteTemplateRecord(effectiveTemplateId, deleteConfirmation);
      setIsDeleteDialogOpen(false);
      setDeleteConfirmation('');

      if (result.result === 'deactivated') {
        const nextDraft = { ...draft, isActive: false };
        setDraft(nextDraft);
        setBaselineSignature(createTemplateDraftSignature(nextDraft));
        setLastTemplateSavedAt(new Date().toISOString());
        setFormError(result.reason ?? 'This template is already referenced by saved masses and was archived instead of deleted.');
        void refreshData();
        return;
      }

      if (profile) {
        await deleteTemplateWorkingDraft(profile.id, effectiveTemplateId).catch(() => {
          // Draft cleanup is best-effort only.
        });
      }

      clearTemplateDraftCache(effectiveTemplateId);
      void refreshData();
      setGuardSuppressed(true);
      navigate(templateRoutes.list, { replace: true });
    } catch (nextError) {
      setFormError(nextError instanceof Error ? nextError.message : 'Failed to delete the template.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title={templateId ? `Edit Template: ${draft.name || currentTemplate?.name || 'Mass Template'}` : 'Create Mass Template'}
        description="Build reusable four-party compositions with a protected working draft, explicit template publishing, and safe recovery after refresh or interruption."
        actions={
          <Link to={templateRoutes.list} className="button button--secondary">
            Back to Mass Templates
          </Link>
        }
      />

      {formError ? <DataStatePanel title="Template action needs attention" description={formError} /> : null}

      <TemplateBuilderForm
        mode={templateId ? 'edit' : 'create'}
        draft={draft}
        weapons={state.weapons}
        isSavingTemplate={isSavingTemplate}
        isAutosaving={autosaveStatus === 'saving'}
        isDeleting={isDeleting}
        isTogglingArchive={isTogglingArchive}
        hasSavedTemplate={hasSavedTemplate}
        hasUnsavedChanges={hasUnsavedChanges}
        autosaveStatus={autosaveStatus}
        draftRestoredFrom={draftRestoredFrom}
        lastAutosavedAt={lastAutosavedAt}
        lastTemplateSavedAt={lastTemplateSavedAt}
        autosaveError={autosaveError}
        onDraftChange={setDraft}
        onSave={() => handleSave(false)}
        onSaveAsNew={() => handleSave(true)}
        onStartNew={handleStartNew}
        onDuplicate={handleDuplicate}
        onToggleArchive={handleToggleArchive}
        onDelete={
          hasSavedTemplate
            ? () => {
                setDeleteConfirmation('');
                setIsDeleteDialogOpen(true);
              }
            : undefined
        }
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        title="Delete Template"
        description="Are you sure you want to delete this template? If saved masses already reference it, Woof Woof will archive it instead of removing it."
        confirmLabel="Delete Template"
        confirmVariant="danger"
        confirmationLabel="Type the template name to confirm"
        requiredText={draft.name.trim()}
        confirmationValue={deleteConfirmation}
        onConfirmationValueChange={setDeleteConfirmation}
        isProcessing={isDeleting}
        onClose={() => {
          if (isDeleting) {
            return;
          }

          setIsDeleteDialogOpen(false);
          setDeleteConfirmation('');
        }}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </AppShell>
  );
}

export function TemplateBuilderPage(): JSX.Element {
  const location = useLocation();

  return (
    <ComponentErrorBoundary
      resetKey={location.key}
      onError={(error) => {
        console.error('TemplateBuilderPage crashed while opening the template editor.', error);
      }}
      fallback={(error) => (
        <AppShell>
          <DataStatePanel
            title="Template editor crashed"
            description={error.message || 'An unexpected error stopped the template editor before it finished rendering.'}
          />
        </AppShell>
      )}
    >
      <TemplateBuilderWorkspace />
    </ComponentErrorBoundary>
  );
}
